import { Component } from '@theme/component';

/**
 * @typedef {Object} PricePerWearInfoRefs
 * @property {HTMLButtonElement} trigger
 * @property {HTMLElement} bubble
 * @property {SVGElement} icon
 */

/**
 * Controls the price-per-wear information popover.
 *
 * @extends {Component<PricePerWearInfoRefs>}
 */
class PricePerWearInfo extends Component {
  requiredRefs = ['trigger', 'bubble', 'icon'];

  connectedCallback() {
    super.connectedCallback();
    this.refs.bubble.addEventListener('toggle', this.#handleToggle);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.bubble?.removeEventListener('toggle', this.#handleToggle);
    this.#stopPositionTracking();
  }

  /** @param {MouseEvent} event */
  handleClick(event) {
    event.preventDefault();

    if (this.refs.bubble.matches(':popover-open')) {
      this.closeTooltip();
      return;
    }

    this.#showTooltip();
  }

  /** @param {KeyboardEvent} event */
  handleKeydown(event) {
    if (event.key === 'Escape') this.closeTooltip();
  }

  closeTooltip() {
    if (this.refs.bubble.matches(':popover-open')) this.refs.bubble.hidePopover();
    this.refs.trigger.setAttribute('aria-expanded', 'false');
    this.#stopPositionTracking();
  }

  #showTooltip() {
    if (!this.refs.bubble.matches(':popover-open')) this.refs.bubble.showPopover();

    this.refs.trigger.setAttribute('aria-expanded', 'true');
    this.#positionBubble();
    this.#startPositionTracking();
  }

  #handleToggle = (event) => {
    const isOpen = event.newState === 'open' || this.refs.bubble.matches(':popover-open');
    this.refs.trigger.setAttribute('aria-expanded', String(isOpen));

    if (!isOpen) {
      this.#stopPositionTracking();
      return;
    }

    this.#positionBubble();
    this.#startPositionTracking();
  };

  #startPositionTracking() {
    window.addEventListener('resize', this.#positionBubble);
    window.addEventListener('scroll', this.#positionBubble, true);
  }

  #stopPositionTracking() {
    window.removeEventListener('resize', this.#positionBubble);
    window.removeEventListener('scroll', this.#positionBubble, true);
  }

  #positionBubble = () => {
    if (!this.refs.bubble.matches(':popover-open')) return;

    const iconRect = this.refs.icon.getBoundingClientRect();
    const bubbleRect = this.refs.bubble.getBoundingClientRect();
    const notchSize = iconRect.height / 2;
    const viewportInset = iconRect.height;
    const iconCenter = iconRect.left + iconRect.width / 2;
    const preferredLeft = iconCenter - bubbleRect.width / 2;
    const minimumLeft = viewportInset;
    const maximumLeft = Math.max(minimumLeft, window.innerWidth - viewportInset - bubbleRect.width);
    const bubbleLeft = Math.min(Math.max(preferredLeft, minimumLeft), maximumLeft);
    const notchPosition = Math.min(Math.max(iconCenter - bubbleLeft, notchSize), bubbleRect.width - notchSize);

    this.refs.bubble.style.setProperty('--wuka-ppw-info-notch-position', `${notchPosition}px`);
    this.refs.bubble.style.left = `${bubbleLeft}px`;
    this.refs.bubble.style.top = `${iconRect.top - notchSize}px`;
    this.refs.bubble.style.transform = 'translateY(-100%)';
  };
}

if (!customElements.get('price-per-wear-info')) {
  customElements.define('price-per-wear-info', PricePerWearInfo);
}
